import { useEffect, useMemo, useState } from 'react';
import {
  deleteContract,
  fetchContract,
  restoreContract,
  updateContractCurrentStage,
} from '../../api/contract';
import {
  Contract,
  createDefaultContractStages,
  isWritingStage,
} from '../../types/contract';
import ContractComments from './ContractComments';
import ContractStageEmbed from './ContractStageEmbed';
import ContractStageStepper from './ContractStageStepper';
import './ContractDetail.css';
import './ContractStageEditor.css';

import { reportError } from '../../utils/notify';

interface ContractDetailProps {
  contractId: number;
  onBack: () => void;
  onEdit?: () => void;
  onDeleted?: () => void;
  onRestored?: () => void;
  pendingMode?: boolean;
}

function ContractDetail({
  contractId,
  onBack,
  onEdit,
  onDeleted,
  onRestored,
  pendingMode = false,
}: ContractDetailProps) {
  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [stageUpdating, setStageUpdating] = useState(false);
  const [activeStageIndex, setActiveStageIndex] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchContract(contractId);
        setContract(data);
        const stages =
          data.stages && data.stages.length > 0 ? data.stages : createDefaultContractStages();
        const idx = stages.findIndex(
          (stage) =>
            (data.currentStageId != null && stage.stageId === data.currentStageId) ||
            stage.stageName === data.currentStage
        );
        setActiveStageIndex(idx >= 0 ? idx : 0);
        setError('');
      } catch {
        reportError(setError, '계약서를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [contractId]);

  const stages = useMemo(() => {
    if (!contract) return createDefaultContractStages();
    return contract.stages && contract.stages.length > 0
      ? contract.stages
      : createDefaultContractStages();
  }, [contract]);

  const activeStage = stages[activeStageIndex] ?? stages[0];
  const showWritingBody = isWritingStage(activeStage);

  const handleSelectStage = (index: number) => {
    setActiveStageIndex(index);
  };

  const handleAdvanceStage = async () => {
    if (!contract || stageUpdating) return;
    const currentIndex = stages.findIndex(
      (stage) =>
        (contract.currentStageId != null && stage.stageId === contract.currentStageId) ||
        stage.stageName === contract.currentStage
    );
    const from = currentIndex >= 0 ? currentIndex : 0;
    if (from >= stages.length - 1) return;
    const next = stages[from + 1];
    if (next?.stageId == null) {
      reportError(setError, '단계 정보가 없습니다. 계약서를 다시 열어 주세요.');
      return;
    }
    try {
      setStageUpdating(true);
      const updated = await updateContractCurrentStage(contract.contractId, next.stageId);
      setContract(updated);
      setActiveStageIndex(from + 1);
      setError('');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '진행 단계 변경에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setStageUpdating(false);
    }
  };

  const handleRetreatStage = async () => {
    if (!contract || stageUpdating) return;
    const currentIndex = stages.findIndex(
      (stage) =>
        (contract.currentStageId != null && stage.stageId === contract.currentStageId) ||
        stage.stageName === contract.currentStage
    );
    const from = currentIndex >= 0 ? currentIndex : 0;
    if (from <= 0) return;
    const prev = stages[from - 1];
    if (prev?.stageId == null) {
      reportError(setError, '단계 정보가 없습니다. 계약서를 다시 열어 주세요.');
      return;
    }
    try {
      setStageUpdating(true);
      const updated = await updateContractCurrentStage(contract.contractId, prev.stageId);
      setContract(updated);
      setActiveStageIndex(from - 1);
      setError('');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '진행 단계 변경에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setStageUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!contract) return;
    if (!window.confirm(`「${contract.contractName}」을(를) 삭제하시겠습니까?`)) return;
    try {
      setDeleting(true);
      await deleteContract(contract.contractId);
      onDeleted?.();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '계약서 삭제에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async () => {
    if (!contract) return;
    if (!window.confirm(`「${contract.contractName}」을(를) 복구하시겠습니까?`)) return;
    try {
      setRestoring(true);
      await restoreContract(contract.contractId);
      onRestored?.();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '계약서 복구에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setRestoring(false);
    }
  };

  if (loading) {
    return <p className="contract-detail__hint">불러오는 중...</p>;
  }
  if (!contract) {
    return <p className="contract-detail__error">{error || '계약서를 찾을 수 없습니다.'}</p>;
  }

  const isPending = Boolean(contract.deletedAt) || pendingMode;

  return (
    <div className="contract-detail">
      <header className="contract-detail__header">
        <div className="contract-detail__header-top">
          <div>
            <div className="contract-detail__badges">
              {isPending && <span className="is-pending">삭제 예정</span>}
              {contract.draft && <span className="is-draft">임시저장</span>}
              {contract.urgent && <span className="is-urgent">긴급</span>}
              <span className="is-stage">{contract.currentStage || '-'}</span>
            </div>
            <h2>{contract.contractName}</h2>
            <p>
              문서번호 {contract.documentNo || '-'}
              {contract.contractNo ? ` · 계약번호 ${contract.contractNo}` : ''}
            </p>
          </div>
          <div className="contract-detail__actions">
            {!isPending && contract.canEdit && onEdit && (
              <button type="button" onClick={onEdit}>
                수정
              </button>
            )}
            {!isPending && contract.canDelete && (
              <button type="button" className="is-danger" disabled={deleting} onClick={handleDelete}>
                {deleting ? '삭제 중...' : '삭제'}
              </button>
            )}
            {isPending && contract.canRestore && (
              <button type="button" className="is-restore" disabled={restoring} onClick={handleRestore}>
                {restoring ? '복구 중...' : '복구'}
              </button>
            )}
            <button type="button" className="is-ghost" onClick={onBack}>
              {isPending ? '삭제 예정으로' : '목록으로'}
            </button>
          </div>
        </div>
        <ContractStageStepper
          stages={stages}
          currentStageId={contract.currentStageId}
          currentStageName={contract.currentStage}
          activeIndex={activeStageIndex}
          onSelect={handleSelectStage}
          onAdvance={handleAdvanceStage}
          onRetreat={handleRetreatStage}
          advancing={stageUpdating}
          disabled={stageUpdating}
          canAdvance={!isPending && Boolean(contract.canEdit)}
        />
      </header>

      {error && <p className="contract-detail__error">{error}</p>}

      {isPending && (
        <section className="contract-detail__card contract-detail__pending-info">
          <h3>삭제 정보</h3>
          <dl>
            <div>
              <dt>삭제자</dt>
              <dd>{contract.deletedUserName || '-'}</dd>
            </div>
            <div>
              <dt>삭제일시</dt>
              <dd>{formatDateTime(contract.deletedAt)}</dd>
            </div>
            <div>
              <dt>자동삭제 예정</dt>
              <dd className="is-purge">{formatDateTime(contract.purgeAt)}</dd>
            </div>
          </dl>
        </section>
      )}

      {!showWritingBody ? (
        <ContractStageEmbed stage={activeStage} contractId={contract.contractId} />
      ) : (
        <>
          <div className="contract-detail__grid">
            <section className="contract-detail__card">
              <h3>계약 정보</h3>
              <dl>
                <div>
                  <dt>문서번호</dt>
                  <dd>{contract.documentNo || '-'}</dd>
                </div>
                <div>
                  <dt>계약번호</dt>
                  <dd>{contract.contractNo || '-'}</dd>
                </div>
                <div>
                  <dt>계약기간</dt>
                  <dd>{formatPeriod(contract.startDate, contract.endDate)}</dd>
                </div>
                <div>
                  <dt>납품예정일</dt>
                  <dd>{contract.deliveryDueDate || '-'}</dd>
                </div>
                <div>
                  <dt>사내 담당자</dt>
                  <dd>{contract.managerName || '-'}</dd>
                </div>
                <div>
                  <dt>금액</dt>
                  <dd>{formatWon(contract.totalAmount ?? 0)}</dd>
                </div>
                <div>
                  <dt>등록자</dt>
                  <dd>{contract.createdUserName || '-'}</dd>
                </div>
                <div>
                  <dt>수정자</dt>
                  <dd>{contract.lastUpdatedUserName || '-'}</dd>
                </div>
              </dl>
            </section>

            <section className="contract-detail__card">
              <h3>발주처</h3>
              <dl>
                <div>
                  <dt>발주처</dt>
                  <dd>{contract.clientName || '-'}</dd>
                </div>
                <div>
                  <dt>담당자</dt>
                  <dd>{contract.clientManagerName || '-'}</dd>
                </div>
                <div>
                  <dt>연락처</dt>
                  <dd>{contract.clientManagerPhone || '-'}</dd>
                </div>
              </dl>
            </section>

            <section className="contract-detail__card">
              <h3>납품 정보</h3>
              <dl>
                <div>
                  <dt>납품장소</dt>
                  <dd>{contract.deliveryPlace || '-'}</dd>
                </div>
                <div>
                  <dt>납품담당자</dt>
                  <dd>{contract.deliveryManagerName || '-'}</dd>
                </div>
                <div>
                  <dt>연락처</dt>
                  <dd>{contract.deliveryManagerPhone || '-'}</dd>
                </div>
              </dl>
            </section>

            <section className="contract-detail__card is-full">
              <h3>제품 내역</h3>
              {!contract.items || contract.items.length === 0 ? (
                <p className="contract-detail__hint">등록된 제품이 없습니다.</p>
              ) : (
                <table className="contract-detail__table">
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>제품명</th>
                      <th>단위</th>
                      <th>수량</th>
                      <th>단가</th>
                      <th>금액</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contract.items.map((item, index) => (
                      <tr key={item.contractItemId ?? index}>
                        <td>{index + 1}</td>
                        <td>
                          {item.productName}
                          {item.productId != null ? (
                            <span className="contract-detail__product-link">연동</span>
                          ) : null}
                        </td>
                        <td>{item.unit || 'EA'}</td>
                        <td>{item.quantity}</td>
                        <td>{formatWon(item.unitPrice ?? 0)}</td>
                        <td>
                          {formatWon(item.amount ?? (item.quantity || 0) * (item.unitPrice || 0))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>

          <div className="contract-detail__comments">
            <ContractComments contractId={contract.contractId} enabled readOnly={isPending} />
          </div>
        </>
      )}
    </div>
  );
}

function formatPeriod(start?: string | null, end?: string | null) {
  if (!start && !end) return '-';
  return `${start || '?'} ~ ${end || '?'}`;
}

function formatWon(value: number) {
  return `${value.toLocaleString('ko-KR')}원`;
}

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ko-KR');
}

export default ContractDetail;
